import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { motion } from "framer-motion";

export interface IMultiSelectItem {
  id?: string;
  name: string;
  value: string | number | boolean | null;
}

interface IMultiSelectProps {
  list: IMultiSelectItem[];
  placeholder?: string;
  selected?: string[] | number[];
  onChange: (selected: any[]) => void;
  label?: string;
  required?: boolean;
  description?: string;
  error?: string;
  helperText?: string;
  selectInputClassName?: string;
  multiSelect?: boolean;
  onQueryChange?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  allowCustomValue?: boolean;
}

export function MultiSelect(props: IMultiSelectProps) {
  const [selected, setSelected] = useState<IMultiSelectItem[]>(
    props.selected
      ? props.selected?.map(
        (value: any): IMultiSelectItem => ({ name: value, value: value })
      )
      : []
  );
  const [query, setQuery] = useState("");
  // Tracks "the user just clicked an option" so the afterLeave callback below
  // does not turn the in-flight typed query into a custom value and clobber
  // the real selection. Lives in a ref so it's immune to closure timing.
  const justSelectedRef = useRef(false);

  const {
    placeholder = "Select option",
    required = false,
    multiSelect = true,
    disabled = false,
    allowCustomValue = true,
  } = props;

  const filteredList =
    query === ""
      ? props.list
      : props.list?.filter((listItem: IMultiSelectItem) =>
        listItem.name
          .toLowerCase()
          .replace(/\s+/g, "")
          .includes(query.toLowerCase().replace(/\s+/g, ""))
      );

  useEffect(() => {
    props.onChange(selected?.map((selectedItem: any) => selectedItem?.value));
  }, [selected]);

  // Sync the internal `selected` state whenever the caller-supplied
  // props.selected changes. Without this, controlled forms that
  // reinitialise (Formik enableReinitialize — e.g. the fleet edit
  // vehicle picker swapping the currently-edited car) update
  // values.make in the parent but the MultiSelect displays whatever
  // was on mount indefinitely, because the internal state was only
  // seeded from props once. Compare stringified values so we don't
  // fire the sync every render when the array reference changes but
  // the contents haven't.
  useEffect(() => {
    // Cast to any[] before .filter — props.selected is typed as
    // string[] | number[], and Array.prototype.filter's overloads
    // aren't compatible across a union of two array element types.
    // The internal state carries IMultiSelectItem wrappers around
    // either element type so any[] is safe here.
    const source: any[] = (props.selected ?? []) as any[];
    const incoming = source
      .filter((v) => v != null && v !== '')
      .map((v) => String(v));
    const current = selected.map((item: IMultiSelectItem) => String(item.value));
    if (incoming.length !== current.length
      || incoming.some((v, i) => v !== current[i])) {
      setSelected(
        source
          .filter((v) => v != null && v !== '')
          .map((v): IMultiSelectItem => ({ name: v, value: v }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selected]);

  useEffect(() => {
    props.onQueryChange?.(query);
  }, [query]);

  const handleDropdownClose = () => {
    // If a real option was just clicked, skip the custom-value path entirely —
    // otherwise the stale typed query (e.g. "Toy") would overwrite the real
    // selection (e.g. "TOYOTA") here.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      setQuery("");
      return;
    }
    if (query && query.trim() !== "" && allowCustomValue) {
      if (multiSelect) {
        setSelected([...selected, { name: query, value: query }]);
      } else {
        setSelected([{ name: query, value: query }]);
      }
    }
    setQuery("");
  };

  const isSelected = (listItem: IMultiSelectItem): boolean => {
    return (
      selected.find(
        (item: IMultiSelectItem) => item.value === listItem.value
      ) !== undefined
    );
  };

  const removeSelectedItem = (listItem: IMultiSelectItem) => {
    setSelected(
      selected.filter((item: IMultiSelectItem) => item.value !== listItem.value)
    );
  };

  return (
    <div className="">
      <Combobox
        value={selected}
        onChange={(value: any) => {
          // Mark the selection BEFORE state updates — afterLeave reads this
          // ref and skips its custom-value branch, so the user's pick wins
          // even if the dropdown's leave animation outraces React rendering.
          justSelectedRef.current = true;
          if (multiSelect) {
            setSelected([...selected, value]);
          } else {
            setSelected([value]);
          }
          // Clear the typed query so the afterLeave handler below doesn't
          // turn the typed text into a custom value and clobber the
          // selection the user just made.
          setQuery("");
        }}
        disabled={disabled}
      >
        {/* Label */}
        {props.label && (
          <Combobox.Label
            className={clsx("block text-sm font-medium text-gray-700 dark:text-darkBg-mainText", {
              ["!text-danger-main"]: props.error && required,
            })}
          >
            {props.label}{" "}
            <span className="!text-danger-main">{required && "*"}</span>
            {props.description && (
              <p
                className={clsx("text-xs text-[#455A64] dark:text-darkBg-subText italic", {
                  ["!text-danger-border"]: props.error && required,
                })}
              >
                {props.description}
              </p>
            )}
          </Combobox.Label>
        )}
        <div className="relative mt-1">
          {/* Selected Items Display */}
          {multiSelect && selected.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selected.slice(0, selected.length > 3 ? 2 : 3).map((listItem: IMultiSelectItem, index: number) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-surface text-primary-main border border-primary-border rounded-full text-2xs font-medium transition-all duration-200 hover:bg-primary-main hover:text-white group"
                >
                  <span>{listItem.name}</span>
                  <button
                    type="button"
                    onClick={() => removeSelectedItem(listItem)}
                    className="ml-1 p-0.5 rounded-full transition-colors duration-200 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-1"
                    aria-label={`Remove ${listItem.name}`}
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {selected.length > 3 && (
                <div className="inline-flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-darkBg-border rounded-full text-sm font-medium">
                  +{selected.length - 2} more
                </div>
              )}
              {/* <div className="text-xs text-gray-500 self-center">
                {selected.length} {selected.length === 1 ? 'item' : 'items'} selected
              </div> */}
            </div>
          )}
          
          <div className="relative w-full cursor-default overflow-hidden rounded-full bg-white dark:bg-darkBg text-left focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 sm:text-sm border-[1px] border-gray-300 dark:border-darkBg-border focus:outline-none focus:ring-2 focus:ring-primary-main focus:border-primary-main transition-all duration-200">
            <div className="flex items-center py-3 px-4">
              <Combobox.Input
                className={clsx(
                  "border-none py-0 px-0 text-sm leading-5 text-gray-900 dark:text-darkBg-mainText focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-darkBg-subText w-full bg-transparent",
                  props.selectInputClassName
                )}
                displayValue={multiSelect ? () => query : () => selected[0]?.name}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={multiSelect && selected.length > 0 ? "Add more items..." : placeholder}
                onClick={() => {
                  document.getElementById("comboInput")?.click();
                }}
              />
              <Combobox.Button
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                id="comboInput"
              >
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400 dark:text-darkBg-subText transition-transform duration-200"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={handleDropdownClose}
          >
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-darkBg py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
              {filteredList?.length === 0 && query !== "" && !props.loading ? (
                <>
                  {allowCustomValue ? (
                    <Combobox.Option
                      value={{ name: query, value: query }}
                      className="cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 dark:text-darkBg-mainText"
                    >
                      {`continue with "${query}"`}
                    </Combobox.Option>
                  ) : <span className="py-2 pl-10 pr-4 text-gray-900 dark:text-darkBg-mainText">No results found</span>}
                </>
              ) : (
                filteredList
                  ?.filter((listItem) => !isSelected(listItem))
                  .map((listItem) => (
                    <Combobox.Option
                      key={listItem.id ?? String(listItem.value)}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-primary-surface dark:bg-primary-surface/20" : "text-gray-900 dark:text-darkBg-mainText"
                        }`
                      }
                      value={listItem}
                    >
                      {({ active }) => (
                        <>
                          <span
                            className={`block truncate ${active ? "font-medium" : "font-normal"
                              }`}
                          >
                            {listItem.name}
                          </span>
                        </>
                      )}
                    </Combobox.Option>
                  )
                  )
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {/* Helper Text */}
      <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: props.error })}>
        {props.helperText && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center font-medium"
          >
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {props.helperText && typeof props.helperText === 'string' ? props.helperText : undefined}
          </motion.p>
        )}
      </div>
    </div>
  );
}
